import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { portal as portalApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PortalActionItem from '../components/PortalActionItem';
import PortalActionForm from '../components/PortalActionForm';
import { ACTION_STATUSES } from '../utils/portal';

/**
 * Every action item across every meeting, either as a flat filtered list or
 * grouped by who owns it — the "who's doing what" view.
 */
export default function PortalActionsPage() {
  const { user, isAdmin, isChair } = useAuth();
  const canManage = isAdmin || isChair;

  const [actions, setActions] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [filters, setFilters] = useState({ status: 'open', assignee_id: '', meeting_id: '' });
  const [groupByPerson, setGroupByPerson] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingAction, setEditingAction] = useState(null);

  const load = useCallback(async () => {
    try {
      setActions(await portalApi.listActions(filters));
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([portalApi.listVolunteers(), portalApi.listMeetings()])
      .then(([v, m]) => { setVolunteers(v); setMeetings(m); })
      .catch((err) => setMessage(`Error: ${err.message}`));
  }, []);

  useEffect(() => {
    if (!canManage) return;
    portalApi.listSegments().then(setSegments).catch(() => {});
  }, [canManage]);

  const saveAction = async (form) => {
    if (editingAction) await portalApi.updateAction(editingAction.id, form);
    else await portalApi.createAction(form);
    setShowForm(false);
    setEditingAction(null);
    await load();
  };

  const setFilter = (field) => (e) => setFilters({ ...filters, [field]: e.target.value });

  // Group by assignee, with unassigned last and the current user first.
  const groups = [];
  if (groupByPerson) {
    const byPerson = new Map();
    actions.forEach((a) => {
      const key = a.assignee_id || '__unassigned';
      if (!byPerson.has(key)) {
        byPerson.set(key, { key, name: a.assignee_name || 'Unassigned', items: [] });
      }
      byPerson.get(key).items.push(a);
    });
    groups.push(...[...byPerson.values()].sort((a, b) => {
      if (a.key === '__unassigned') return 1;
      if (b.key === '__unassigned') return -1;
      if (a.key === user?.id) return -1;
      if (b.key === user?.id) return 1;
      return a.name.localeCompare(b.name);
    }));
  }

  return (
    <div className="portal-page">
      <div className="portal-header">
        <div>
          <h1>Action Items</h1>
          <p className="portal-subtitle"><Link to="/volunteer">← Volunteers</Link></p>
        </div>
        {canManage && (
          <button className="btn-sm btn-primary" onClick={() => { setEditingAction(null); setShowForm(true); }}>
            Add Action Item
          </button>
        )}
      </div>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      <div className="portal-filters">
        <label>
          Status
          <select value={filters.status} onChange={setFilter('status')}>
            <option value="open">Open (any stage)</option>
            <option value="">All</option>
            {ACTION_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>

        <label>
          Assignee
          <select value={filters.assignee_id} onChange={setFilter('assignee_id')}>
            <option value="">Everyone</option>
            <option value="me">Me</option>
            <option value="unassigned">Unassigned</option>
            {volunteers.map((v) => <option key={v.id} value={v.id}>{v.display_name}</option>)}
          </select>
        </label>

        <label>
          Meeting
          <select value={filters.meeting_id} onChange={setFilter('meeting_id')}>
            <option value="">Any meeting</option>
            <option value="none">Not tied to a meeting</option>
            {meetings.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
        </label>

        <label className="portal-filter-toggle">
          <input type="checkbox" checked={groupByPerson} onChange={(e) => setGroupByPerson(e.target.checked)} />
          Group by person
        </label>
      </div>

      {loading ? (
        <div className="portal-muted">Loading…</div>
      ) : actions.length === 0 ? (
        <div className="portal-empty"><p>No action items match these filters.</p></div>
      ) : groupByPerson ? (
        groups.map((group) => (
          <section className="portal-section" key={group.key}>
            <h2 className="portal-group-heading">
              {group.key === '__unassigned' ? 'Unassigned' : group.name}
              {group.key === user?.id && <span className="portal-you-badge">you</span>}
              <span className="portal-group-count">{group.items.length}</span>
            </h2>
            <div className="portal-action-list">
              {group.items.map((a) => (
                <PortalActionItem
                  key={a.id}
                  action={a}
                  onChanged={load}
                  onEdit={(item) => { setEditingAction(item); setShowForm(true); }}
                />
              ))}
            </div>
          </section>
        ))
      ) : (
        <div className="portal-action-list">
          {actions.map((a) => (
            <PortalActionItem
              key={a.id}
              action={a}
              onChanged={load}
              onEdit={(item) => { setEditingAction(item); setShowForm(true); }}
            />
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingAction(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <PortalActionForm
              initial={editingAction}
              volunteers={volunteers}
              segments={segments}
              meetings={meetings}
              onSave={saveAction}
              onCancel={() => { setShowForm(false); setEditingAction(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
